import { Image } from 'expo-image';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useSlackAuthorize } from '@/api/partners/integrations/slack/use-slack-authorize';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { GREY_10 } from '@/style/colors';
import { showErrorMessage, Text } from '@/ui';
import { EonIcon } from '@/ui/icons/eon-icon';

import { SLACK_ICON_URL } from '../constants';

type Props = {
  visible: boolean;
  onClose: () => void;
};

export function SlackConnectModal({ visible, onClose }: Props) {
  const { styles } = useStyles(stylesheet);
  const { mutateAsync: authorizeSlack, isPending: isAuthorizing } =
    useSlackAuthorize();

  const handleConnect = async () => {
    try {
      const response = await authorizeSlack();
      const authorizeUrl =
        response.data?.authorizeUrl ??
        (response as { authorizeUrl?: string }).authorizeUrl;

      if (!authorizeUrl) {
        throw new Error('Could not get Slack authorization URL');
      }

      if (Platform.OS === 'web') {
        window.location.href = authorizeUrl;
      }
    } catch (error) {
      showErrorMessage(
        error instanceof Error ? error.message : 'Could not connect to Slack'
      );
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={visible}
      hasBackdrop
      style={styles.container}
      onBackdropPress={onClose}
    >
      <View style={styles.containerView}>
        <NavigationButton
          type={NavigationButtonTypes.cancel}
          onPress={onClose}
          style={styles.closeButton}
        />

        <View style={styles.content}>
          <View style={styles.logoRow}>
            <Image
              source={{ uri: SLACK_ICON_URL }}
              style={styles.appIcon}
              contentFit="cover"
            />
            <View style={styles.logoConnector} />
            <View style={styles.eonIconContainer}>
              <EonIcon width={32} height={32} />
            </View>
          </View>

          <View style={styles.textBlock}>
            <Text type="title2" style={styles.title}>
              Connect Your EonOne with Slack
            </Text>

            <Text type="bodyRegular" style={styles.description}>
              Enable team collaboration with real-time updates from EonOne
              directly within Slack. Ensure your team stays aligned and informed
              effortlessly.
            </Text>
          </View>

          <View style={styles.footer}>
            <Pressable
              onPress={handleConnect}
              disabled={isAuthorizing}
              style={({ pressed }) => [
                styles.connectButton,
                pressed && styles.connectButtonPressed,
              ]}
            >
              {isAuthorizing ? (
                <ActivityIndicator color={GREY_10} />
              ) : (
                <>
                  <Image
                    source={{ uri: SLACK_ICON_URL }}
                    style={styles.connectButtonIcon}
                    contentFit="cover"
                  />
                  <Text type="title2" style={styles.connectButtonText}>
                    Sign in with Slack
                  </Text>
                </>
              )}
            </Pressable>

            <Text type="caption2" style={styles.footerText}>
              You'll be taken to choose a workspace and approve access.
            </Text>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    marginTop: 48,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 585,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    zIndex: 1,
  },
  content: {
    padding: 24,
    gap: 24,
    alignItems: 'center',
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  appIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
  },
  logoConnector: {
    width: 24,
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  eonIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: 'rgba(4, 5, 5, 0.9)',
    borderWidth: 1,
    borderColor: '#464646',
    alignItems: 'center',
    justifyContent: 'center',
  },
  textBlock: {
    gap: 4,
    alignItems: 'center',
    width: '100%',
  },
  title: {
    textAlign: 'center',
  },
  description: {
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
  footer: {
    gap: 8,
    alignItems: 'center',
    width: '100%',
  },
  connectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    width: 256,
    height: 48,
    borderRadius: 48,
    backgroundColor: '#4A154B',
  },
  connectButtonPressed: {
    opacity: 0.85,
  },
  connectButtonIcon: {
    width: 20,
    height: 20,
    borderRadius: 4,
  },
  connectButtonText: {
    color: GREY_10,
  },
  footerText: {
    color: theme.colors.text.secondary,
    textAlign: 'center',
  },
}));
